import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Truck, Mail, Lock, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AccessibleFormField from '../components/AccessibleFormField';

const DriverLoginPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      const result = await login(data.email, data.password);
      
      if (result.success) {
        toast.success('התחברות מוצלחת!');
        navigate('/driver/dashboard');
      } else {
        // Handle account lockout specifically
        if (result.message && result.message.includes('locked')) {
          toast.error(result.message, { duration: 10000 });
        } else {
          toast.error(result.message);
        }
      }
    } catch (error) {
      toast.error('שגיאה בהתחברות');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Skip link targets */}
        <div id="main-content" className="sr-only" tabIndex="-1">תוכן ראשי</div>
        
        {/* Header */}
        <header id="navigation" className="text-center" tabIndex="-1">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-orange-600 hover:text-orange-700 mb-6 mx-auto text-lg font-semibold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
          >
            <ArrowLeft className="h-5 w-5 ml-3" aria-hidden="true" />
            חזור לדף הבית
          </button>
          
          <div className="flex justify-center mb-6">
            <div className="bg-orange-100 p-4 rounded-full">
              <Truck className="h-16 w-16 text-orange-600" aria-hidden="true" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-brown-900 mb-4">
            התחברות מובילים
          </h1>
          <p className="text-lg text-brown-700">
            התחבר לחשבון שלך כדי לקבל בקשות הובלה
          </p>
        </header>

        {/* Login Form */}
        <main className="card hover-lift">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} aria-label="טופס התחברות">
            <div className="space-y-6">
              <AccessibleFormField
                label="כתובת אימייל"
                id="email"
                error={errors.email?.message}
                required={true}
                helpText="הזן את כתובת האימייל שלך"
              >
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <div className="bg-orange-100 p-2 rounded-full">
                      <Mail className="h-4 w-4 text-orange-600" aria-hidden="true" />
                    </div>
                  </div>
                  <input
                    type="email"
                    {...register('email', {
                      required: 'כתובת האימייל היא שדה חובה',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'כתובת האימייל אינה תקינה'
                      }
                    })}
                    maxLength={100}
                    className="input-field pr-12"
                    placeholder="הזן את כתובת האימייל שלך"
                  />
                </div>
              </AccessibleFormField>

              <AccessibleFormField
                label="סיסמה"
                id="password"
                error={errors.password?.message}
                required={true}
                helpText="הזן את הסיסמה שלך"
              >
                <div className="relative">
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <div className="bg-orange-100 p-2 rounded-full">
                      <Lock className="h-4 w-4 text-orange-600" aria-hidden="true" />
                    </div>
                  </div>
                  <input
                    type="password"
                    {...register('password', {
                      required: 'הסיסמה היא שדה חובה'
                    })}
                    maxLength={128}
                    className="input-field pr-12"
                    placeholder="הזן את הסיסמה שלך"
                  />
                </div>
              </AccessibleFormField>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed hover-lift focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2" aria-hidden="true"></div>
                    מתחבר...
                  </div>
                ) : (
                  'התחבר'
                )}
              </button>
            </div>

            <nav className="text-center space-y-4" aria-label="קישורים נוספים">
              <div>
                <Link
                  to="/driver/password-reset"
                  className="text-orange-600 hover:text-orange-700 font-semibold underline focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  שכחת את הסיסמה?
                </Link>
              </div>
              <div>
                <p className="text-brown-600">
                  אין לך חשבון?{' '}
                  <Link
                    to="/driver/register"
                    className="text-orange-600 hover:text-orange-700 font-semibold underline focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  >
                    הירשם עכשיו
                  </Link>
                </p>
              </div>
            </nav>
          </form>
        </main>
      </div>
    </div>
  );
};

export default DriverLoginPage;
